// Device-only history. No transcript is uploaded by this module.
export const CHAT_TTL = 7 * 86400000;
export const CHAT_BYTES = 5 * 1024 * 1024;
let activeUid = null;
let generation = 0;
let queue = Promise.resolve();
const serialize = fn => { const next = queue.then(fn); queue = next.catch(() => {}); return next; };
export function historyEnabled() {
    try { return localStorage.getItem('btl-chat-history') !== 'off'; } catch { return false; }
}
function openStore() {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open('btl-local-chat', 1);
        request.onupgradeneeded = () => request.result.createObjectStore('chats', { keyPath: 'id' });
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}
async function transaction(mode, action) {
    const db = await openStore();
    try {
        return await new Promise((resolve, reject) => {
            const tx = db.transaction('chats', mode);
            let result;
            action(tx.objectStore('chats'), value => { result = value; });
            tx.oncomplete = () => resolve(result);
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error);
        });
    } finally { db.close(); }
}
export function boundedChats(rows, now = Date.now()) {
    const valid = rows.filter(r => r.updatedAt > now - CHAT_TTL && Array.isArray(r.messages))
        .sort((a, b) => b.updatedAt - a.updatedAt);
    let bytes = 0;
    return valid.filter(r => { bytes += new TextEncoder().encode(JSON.stringify(r)).length; return bytes <= CHAT_BYTES; });
}
export function setHistoryAccount(uid) {
    if (activeUid === uid) return queue;
    activeUid = uid || null;
    generation++;
    // Also removes another account's history after a browser restart/account switch.
    return serialize(() => transaction('readwrite', store => {
        const read = store.getAll();
        read.onsuccess = () => read.result.forEach(r => { if (r.uid !== uid || !uid || r.updatedAt <= Date.now() - CHAT_TTL) store.delete(r.id); });
    })).catch(() => {});
}
export function loadChat(uid, packId) {
    const epoch = generation;
    return serialize(async () => {
        if (!uid || uid !== activeUid || epoch !== generation || !historyEnabled()) return [];
        const row = await transaction('readonly', (store, done) => {
            const request = store.get(JSON.stringify([uid, packId]));
            request.onsuccess = () => done(request.result);
        });
        return epoch === generation && row?.updatedAt > Date.now() - CHAT_TTL ? row.messages : [];
    });
}
export function saveChat(uid, packId, messages) {
    const epoch = generation;
    return serialize(async () => {
        if (!uid || uid !== activeUid || epoch !== generation || !historyEnabled()) return;
        await transaction('readwrite', store => {
            const read = store.getAll();
            read.onsuccess = () => {
                if (epoch !== generation) return;
                const row = { id: JSON.stringify([uid, packId]), uid, packId, updatedAt: Date.now(),
                    messages: messages.filter(m => ['user', 'assistant', 'error'].includes(m.role)).slice(-100)
                        .map(m => ({ role: m.role, text: String(m.text || '').slice(0, 24000) })) };
                const keep = boundedChats([row, ...read.result.filter(r => r.id !== row.id && r.uid === uid)]);
                const ids = new Set(keep.map(r => r.id));
                read.result.forEach(r => { if (!ids.has(r.id)) store.delete(r.id); });
                if (ids.has(row.id)) store.put(row);
            };
        });
    });
}
export function clearChat(uid, packId) {
    return serialize(() => transaction('readwrite', store => store.delete(JSON.stringify([uid, packId]))));
}
export function setHistoryEnabled(enabled) {
    try { localStorage.setItem('btl-chat-history', enabled ? 'on' : 'off'); } catch {}
    generation++;
    window.dispatchEvent(new Event('btl-history-preference'));
    if (!enabled) return serialize(() => transaction('readwrite', store => store.clear()));
    return Promise.resolve();
}
